import { HttpStatusCode } from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SERVICE_TEMPLATE_ID } from "../../../../consts/serviceTemplate";

import { ReturnStoreType } from "../../../../types/goods";
import { SERVICE_REQUEST_TIMEOUT } from "../../../../types/rentTypes";
import { BuildingInfo } from "../../../../types/member";

import {
  formatDiffSecondToString,
  remainTime,
} from "../../../../libs/dateFormat";

import {
  cancelGoodsRental,
  completeGoodsRental,
  rentGoodsRental,
} from "../../../../services/goodsRentalServices";

import useSimplePopup from "../../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";

import CommonButton from "../../../Button";
import CommonPopup from "../../../Popup";
import SpaceScannerPopup from "../../../Popup/SpaceScanner";

type Props = {
  url?: string;
  activityCount?: number;
  status?: string;
  goodsServiceId?: string;
  onLoad?: () => void;
  storeId?: string;
  createdAt?: string;
  rentalAt?: string;
  stockId?: string;
  buildingInfo?: Partial<BuildingInfo>;
  returnStoreList?: ReturnStoreType[];
};

export default function ServiceRentDetailHeader({
  url,
  activityCount,
  status,
  goodsServiceId,
  onLoad,
  storeId,
  createdAt,
  rentalAt,
  stockId,
  buildingInfo,
  returnStoreList,
}: Props) {
  const [popup, setPopup] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCancel, setIsLoadingCancel] = useState(false);
  const [simplePopupComponent, { open, openError }] = useSimplePopup({});
  const [scannerTitle, setScannerTitle] = useState("");
  const navigate = useNavigate();
  const [remainString, setRemainString] = useState<string>("");
  const loadingComponent = useFullScreenLoading({
    isLoading:
      isLoading ||
      ((status === "REQUESTED" ||
        status === "RENTED" ||
        status === "EXPIRED") &&
        remainString.length === 0),
    testId: "service-rent-detail-header",
  });
  const isLoaded = useRef(false);

  const handleCancel = async () => {
    setIsLoadingCancel(true);
    try {
      // await patchSpaceReservationCancel(rentDetail?.rentDetailId as string);\
      await cancelGoodsRental(goodsServiceId as string);
      open("예약 취소에 성공했습니다.", {
        onClick: async () => {
          await onLoad?.();
          await setPopup("");
          setIsLoadingCancel(false);
        },
      });
    } catch (e: any) {
      openError(e, "예약 취소에 실패했습니다.");
    } finally {
      setIsLoadingCancel(false);
    }
  };

  const getHeaderStatusScreen = () => {
    if (status === "REQUESTED") {
      return (
        <div className="pt-10 pb-5 flex flex-col gap-2.5 w-full">
          <div
            className="min-h-[100px] flex justify-center items-center flex-[1_0_0] self-stretch leading-[21px] gap-1 font-medium design_min:flex-wrap design_min:px-5"
            data-testid="remain-string-block"
          >
            <span>대여 예약 시간이</span>
            <span className="text-brand-primary-magenta font-bold">
              {remainString}
            </span>
            <span>남았어요</span>
          </div>
          <div className="px-5 h-bottom-button flex gap-2.5 self-stretch w-full">
            <CommonButton
              variant="secondary"
              className="rounded-[3px] text-sm h-bottom-button flex-1"
              onClick={() => setPopup("cancelConfirm")}
            >
              예약 취소
            </CommonButton>
            <CommonButton
              className="rounded-[3px] text-sm h-bottom-button flex-1"
              onClick={() => {
                setScannerTitle("대여하기");
                setPopup("spaceScanner");
              }}
            >
              대여하기
            </CommonButton>
          </div>
        </div>
      );
    }

    if (status === "RENTED") {
      if (remainTime(rentalAt ?? "", 0) > 0) {
        return (
          <div className="pt-10 pb-5 flex flex-col gap-2.5 w-full">
            <div
              className="min-h-[100px] flex justify-center items-center flex-[1_0_0] self-stretch leading-[21px] gap-1 font-medium design_min:flex-wrap design_min:px-5"
              data-testid="remain-string-block"
            >
              <span>대여 마감까지</span>
              <span className="text-brand-primary-magenta font-bold">
                {remainString}
              </span>
              <span>남았어요</span>
            </div>
            <div className="px-5 h-bottom-button flex gap-2.5 self-stretch w-full">
              <CommonButton
                className="rounded-[3px] text-sm h-bottom-button flex-1"
                onClick={() => {
                  setScannerTitle("반납하기");
                  setPopup("spaceScanner");
                }}
              >
                반납하기
              </CommonButton>
            </div>
          </div>
        );
      } else {
        return (
          <div className="pt-10 pb-5 flex flex-col gap-2.5 w-full">
            <div
              className="min-h-[100px] flex justify-center items-center flex-[1_0_0] self-stretch flex-col gap-2.5"
              data-testid="remain-string-block"
            >
              <div className="leading-[21px] flex justify-center gap-1 font-medium design_min:flex-wrap design_min:px-5">
                <span>반납 기한이</span>
                <span className="text-brand-primary-magenta font-bold">
                  {remainString} 초과
                </span>
                <span>되었어요</span>
              </div>
              <div className="h-min-[31px] text-xs leading-4 tracking-[-0.12px]">
                연체 기간이 2일이 넘어가면 분실처리되요
              </div>
            </div>
            <div className="px-5 h-bottom-button flex gap-2.5 self-stretch w-full">
              {/* <CommonButton
                variant="secondary"
                className="rounded-[3px] text-sm h-bottom-button flex-1"
                onClick={() => navigate(`/history/rent/${goodsServiceId}`)}
              >
                문의하기
              </CommonButton> */}
              <CommonButton
                className="rounded-[3px] text-sm h-bottom-button flex-1"
                onClick={() => {
                  setScannerTitle("반납하기");
                  setPopup("spaceScanner");
                }}
              >
                반납하기
              </CommonButton>
            </div>
          </div>
        );
      }
    }

    if (status === "LOST_DAMAGED") {
      return (
        <div className="pt-10 pb-5 flex flex-col gap-2.5 w-full">
          <div
            className="min-h-[100px] flex justify-center items-center flex-[1_0_0] self-stretch flex-col gap-2.5"
            data-testid="remain-string-block"
          >
            <div className="leading-[21px]">
              <span className="text-brand-primary-magenta font-bold">
                분실 상태
              </span>
              &nbsp;
              <span className="font-medium">에요</span>
            </div>
            <div className="h-min-[31px] text-xs leading-4 tracking-[-0.12px] whitespace-pre-line text-center">
              {
                "분실 상태 시 다른 대여 서비스를 이용하지 못해요\n담당자에게 문의해주세요"
              }
            </div>
          </div>
          <div className="px-5 h-bottom-button flex gap-2.5 self-stretch w-full">
            <CommonButton
              className="rounded-[3px] text-sm h-bottom-button flex-1"
              onClick={() =>
                navigate("/report", {
                  state: {
                    ...buildingInfo,
                    serviceTemplateId: SERVICE_TEMPLATE_ID.ETC,
                    parentUrl: `/service/rental/${stockId}`,
                  },
                })
              }
            >
              문의하기
            </CommonButton>
          </div>
        </div>
      );
    }

    if ((activityCount ?? 0) <= 0) {
      return "대여 가능한 수량이 없어요";
    }

    return undefined;
  };

  useEffect(() => {
    let timeInterval: NodeJS.Timeout;
    if (status === "REQUESTED") {
      timeInterval = setInterval(() => {
        if (remainTime(createdAt ?? "", SERVICE_REQUEST_TIMEOUT) > 0) {
          setRemainString(
            formatDiffSecondToString(
              remainTime(createdAt ?? "", SERVICE_REQUEST_TIMEOUT) ?? 0,
              { includesSeconds: true }
            )
          );
        } else if (isLoaded.current === false) {
          isLoaded.current = true;
          onLoad?.();
        }
      }, 1000);
    }

    if (status === "RENTED") {
      timeInterval = setInterval(() => {
        setRemainString(
          formatDiffSecondToString(Math.abs(remainTime(rentalAt ?? "", 0) ?? 0))
        );
      }, 1000);
    }

    if (status === "EXPIRED") {
      timeInterval = setInterval(() => {
        setRemainString(
          formatDiffSecondToString(Math.abs(remainTime(rentalAt ?? "", 0) ?? 0))
        );
      }, 1000);
    }

    return () => {
      clearInterval(timeInterval);
    };
  }, [status]);

  return (
    <>
      {loadingComponent}
      <div
        className="h-[220px] overflow-hidden flex items-center relative"
        data-testid="detail-header-wrapper"
      >
        {url && <img src={url} alt="Service" className="w-full object-cover" />}
        {getHeaderStatusScreen() !== undefined && (
          <div className="absolute inset-0 bg-brand-gray-140 bg-opacity-90 flex items-center justify-center text-white text-lg leading-[21px]">
            {getHeaderStatusScreen()}
          </div>
        )}

        <CommonPopup isOpen={popup === "cancelConfirm"} padding={0}>
          <div className=" whitespace-pre-line p-5 text-center font-medium">
            예약을 취소하시겠습니까?
          </div>
          <div className="flex">
            <CommonButton
              className="w-full rounded-none h-bottom-button"
              variant="secondary"
              onClick={() => setPopup("")}
              disabled={isLoadingCancel}
            >
              취소
            </CommonButton>
            <CommonButton
              className="w-full rounded-none h-bottom-button"
              onClick={handleCancel}
              disabled={isLoadingCancel}
            >
              확인
            </CommonButton>
          </div>
        </CommonPopup>
        <SpaceScannerPopup
          popup={popup}
          onClose={() => setPopup("")}
          title={scannerTitle}
          onResult={async (result) => {
            if (result) {
              let json: any;

              try {
                json = JSON.parse(result);
              } catch (e: any) {
                open(`잘못된 장소 데이터입니다.` + JSON.stringify(e));
                return;
              }

              setIsLoading(true);
              try {
                if (scannerTitle === "대여하기") {
                  if (
                    json.bldgId !== buildingInfo?.buildingId ||
                    json.storeId !== storeId
                  ) {
                    open(
                      "대여를 신청한 장소가 아닙니다.\n대여 장소를 확인해주세요."
                    );
                    return;
                  }

                  const result = await rentGoodsRental(
                    goodsServiceId as string,
                    json
                  );
                  if (result.status === HttpStatusCode.Ok) {
                    navigate("/service/rental/rented", {
                      state: { ...result.data, stockId },
                    });
                  } else {
                    open("대여를 실패했습니다." + JSON.stringify(result));
                  }
                } else if (scannerTitle === "반납하기") {
                  if (
                    !returnStoreList?.find(
                      (item) =>
                        item.buildingId === json.bldgId &&
                        item.storeId === json.storeId
                    )
                  ) {
                    open(
                      "반납 가능한 장소가 아닙니다.\n반납 장소를 확인해주세요."
                    );
                    return;
                  }

                  const result = await completeGoodsRental(
                    goodsServiceId as string,
                    json
                  );
                  if (result.status === HttpStatusCode.Ok) {
                    navigate("/service/rental/returned", {
                      state: { ...result.data, stockId },
                    });
                  } else {
                    open("반납에 실패했습니다. " + JSON.stringify(result));
                  }
                } else {
                  open(`잘못된 요청입니다. (${scannerTitle})`);
                }
              } catch (e: any) {
                if (e?.response?.data?.error) {
                  open(e.response.data.error?.replace(". ", ".\n"));
                } else {
                  open(`${scannerTitle.slice(0, 2)}에 실패했습니다.`);
                }
              } finally {
                setIsLoading(false);
              }
            } else if (!result) {
              open("데이터를 읽어오지 못했습니다.");
            } else {
              open("예상치 못한 에러 케이스: " + result);
            }
          }}
        />
        {simplePopupComponent}
      </div>
    </>
  );
}
